<template>
  <div class="vkb-date-input">
    <label @click.prevent.stop="onOpenDatePicker">
      <input
        ref="year"
        v-model="inputValue"
        :placeholder="placeholder"
        :class="[ 'input', { 'invalid': invalid } ]"
        readonly
        disabled
        type="text"
        @keydown="(e) => validateNumber(e)"
        @input="onInputDate"
      >
      <i class="fo fo-calendar fo-detail" />
    </label>

    <div v-if="error" class="hint-wrapper">
      <div class="hint error open">{{ error }}</div>
    </div>

    <div :class="[ 'date-picker-wrapper', { 'open': !!showDatePicker } ]">
      <!-- linke Seite mit Schließen-Button und Jahr/Monatsauswahl -->
      <div class="date-picker-left">
        <div class="close" @click="onCloseDatePicker">t</div>

        <div class="year select-wrapper block">
          <select
            :value="year"
            class="dropdown block"
            @change="onSelectYear"
          >
            <option
              v-for="_year in yearOptions"
              :key="_year"
            >
              {{ _year }}
            </option>
          </select>
        </div>
        <div class="select-wrapper block month">
          <select
            :value="month"
            class="dropdown block"
            @change="onSelectMonth"
          >
            <option
              v-for="(_month, index) in monthOptions"
              :key="index"
              :value="index"
              :disabled="isDisabledMonth(index)"
            >
              {{ _month }}
            </option>
          </select>
        </div>
      </div>

      <!-- rechte Seite mit Kalender -->
      <div class="date-picker-right">
        <div class="weeks-wrapper">
          <div class="weekdays ">
            <div
              v-for="(weekday, index) in weekdays"
              :key="index"
              class="weekday"
            >
              {{ weekday }}
            </div>
          </div>
          <template v-for="(days, index) in dayOptions">
            <div :key="index" class="days">
              <div
                v-for="(day, index_) in days" :key="index_"
                :class="[
                  'day',
                  { 'selected': day.selected },
                  { 'disabled': isDisabledDate(day.value) },
                  { 'no-date': !day.value }
                ]"
                @click="onSelectDate(day.value)"
              >
                {{ day.value ? day.value : '&nbsp;' }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'vkb-date-picker',

	props: {
    minDate: {
      type: String,
      default: moment('1920-01-01').format('DD.MM.YYYY')
    },
    maxDate: {
      type: String,
      default: moment().format('DD.MM.YYYY')
    },
    value: {
      type: String,
      default: ''
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    invalid: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'TT.MM.JJJJ'
    },
    error: {
      type: String,
      default: ''
    },
    defaultDate: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    monthOptions: moment.months(),
    weekdays: moment.weekdaysMin(true),
    dateContext: moment(),
    inputValue: '',
    showDatePicker: false
  }),

  computed: {
    month() {
      return this.dateContext.month();
    },

    year() {
      return this.dateContext.year();
    },

    selectedDate() {
      if (!this.value) {
        return null;
      }

      let _moment = moment(this.value, this.format);

      return _moment.isValid()
        ? _moment
        : null;
    },

    dayOptions() {
      let _days = this.dateContext.daysInMonth();
      let _first = this.dateContext.startOf('month').get('day') - 1;
      let _last = (7 - this.dateContext.endOf('month').get('day')) % 7;

      _first = _first >= 0 ? _first : 6;

      let calendarDays = [
        ...Array(_first).fill({ value: 0, selected: false }),
        ...Array(_days).fill(0).map((_, k) => ({
            value: k + 1,
            selected: this.selectedDate &&
              k + 1 == this.selectedDate.date() &&
              this.month == this.selectedDate.month() &&
              this.year == this.selectedDate.year()
          })
        ),
        ...Array(_last).fill({ value: 0, selected: false })
      ];

      return new Array(Math.ceil(calendarDays.length / 7))
        .fill()
        .map(() => calendarDays.splice(0, 7));
    },

    yearOptions() {
      return new Array(this.maxDate_.year() - this.minDate_.year() + 1)
        .fill(0)
        .map((n, i) => this.minDate_.year() + i);
    },

    minDate_() {
      return moment(this.minDate, this.format);
    },

    maxDate_() {
      return moment(this.maxDate, this.format);
    }
  },

  created() {
    this.initDateContext();
  },

	methods: {
    initDateContext() {
      this.inputValue = '';

      if (this.value) {
        const moment_ = moment(this.value, this.format);

        this.dateContext = moment_.isValid()
          ? moment_
          : moment();

        this.inputValue = this.dateContext.format(this.format);
      } else if (this.defaultDate) {
        this.dateContext = moment(this.defaultDate, this.format);
      } else {
        this.dateContext = moment();
      }
    },

    isDisabledYear(year) {
      return (
        year < this.minDate_.year() ||
        year > this.maxDate_.year()
      );
    },

    isDisabledMonth(month) {
      if (this.isDisabledYear(this.year)) {
        return true;
      }

      return (
        (month < this.minDate_.month() && this.year === this.minDate_.year()) ||
        (month > this.maxDate_.month() && this.year === this.maxDate_.year())
      );
    },

    isDisabledDate(date) {
      if (this.isDisabledMonth(this.month)) {
        return true;
      }

      return (
        (
          date < this.minDate_.date() &&
          this.month === this.minDate_.month() &&
          this.year === this.minDate_.year()
        ) || (
          date > this.maxDate_.date() &&
          this.month === this.maxDate_.month() &&
          this.year === this.maxDate_.year()
        )
      );
    },

    onOpenDatePicker() {
      this.showDatePicker = !this.showDatePicker && !this.readonly;
      this.$emit('show');
    },

    onCloseDatePicker() {
      this.showDatePicker = false;
      this.$emit('close');
    },

    onInputDate(e) {
      if (!e.target.value || e.target.value.toString().length < 10) {
        this.$emit('input', null);
        return;
      }

      let _moment = moment(e.target.value, this.format);

      if (_moment.isValid()) {
        this.dateContext = _moment;

        this.$emit('input', this.dateContext.format(this.format));
      }
    },

    onSelectDate(date) {
      this.dateContext = moment(this.dateContext).set('date', date);
      this.inputValue = this.dateContext.format(this.format);

      this.$emit('input', this.dateContext.format(this.format));
      this.showDatePicker = false;
    },

    onSelectMonth(e) {
      let month = parseInt(e.target.value);
      this.dateContext = moment(this.dateContext).set('month', month);
    },

    onSelectYear(e) {
      let year = parseInt(e.target.value);
      this.dateContext = moment(this.dateContext).set('year', year);

      this.updateMonth();
    },

    updateMonth() {
      if (this.year === this.maxDate_.year() && this.month > this.maxDate_.month()) {
        this.dateContext = moment(this.dateContext).set('month', this.maxDate_.month());
      }

      if (this.year === this.minDate_.year() && this.month < this.minDate_.month()) {
        this.dateContext = moment(this.dateContext).set('month', this.minDate_.month());
      }
    },

		validateNumber(e) {
			if (
        e.keyCode == 8 ||
        e.keyCode == 46 ||
        e.keyCode == 190 ||
        (
          e.keyCode >= 37 &&
          e.keyCode <= 40
        )
      ) {
				return true ;
      }

			if (e.keyCode == 32 || isNaN(e.key)) {
				e.preventDefault();
			}
    }
  },

  watch: {
    value() {
      this.initDateContext();
    },

    showDatePicker() {
      this.initDateContext();
    }
  }
};
</script>

<style lang="scss">
.vkb-date-input {
  input {
    cursor: default;
    pointer-events: none;
  }

  .invalid {
    color: red !important;
  }

  .fo-calendar {
    cursor: pointer;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/common/vkb-date-picker.vue